// src/hooks/useBCPAISuggestions.ts - AI suggestions logic for BCP
import { useCallback, useRef } from 'react';
import { createBCP, getBCPRiskSuggestions, BCPSuggestions } from '../services/api';
import { FormState, RiskState } from '../types/bcpForm';
import { logger } from '../utils/logger';

// Import default risks for duplicate checking
const defaultRisks = [
  'Natural Disasters',
  'Cyber Attacks',
  'Supply Chain Disruption',
  'Key Personnel Loss',
  'Equipment Failure'
];

const defaultLocationRisks = [
  'Earthquakes',
  'Floods',
  'Hurricanes',
  'Wildfires',
  'Extreme Weather'
];

export interface AISuggestionsHook {
  getAISuggestions: () => Promise<void>;
  applySuggestions: () => void;
  suggestions: BCPSuggestions | null;
  isLoadingSuggestions: boolean;
  suggestionsApplied: boolean;
  setSuggestions: React.Dispatch<React.SetStateAction<BCPSuggestions | null>>;
  setSuggestionsApplied: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useBCPAISuggestions = (
  bcpId: string | undefined,
  formData: FormState,
  risks: RiskState,
  setRisks: React.Dispatch<React.SetStateAction<RiskState>>,
  setFormData: React.Dispatch<React.SetStateAction<FormState>>,
  setBcpId: React.Dispatch<React.SetStateAction<string | undefined>>,
  suggestions: BCPSuggestions | null,
  setSuggestions: React.Dispatch<React.SetStateAction<BCPSuggestions | null>>,
  isLoadingSuggestions: boolean,
  setIsLoadingSuggestions: React.Dispatch<React.SetStateAction<boolean>>,
  suggestionsApplied: boolean,
  setSuggestionsApplied: React.Dispatch<React.SetStateAction<boolean>>
): AISuggestionsHook => {
  const suggestionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper function to check if risks are semantically similar
  const isSimilarRisk = useCallback((risk1: string, risk2: string): boolean => {
    const normalized1 = risk1.toLowerCase().replace(/[^a-z0-9\s]/g, '');
    const normalized2 = risk2.toLowerCase().replace(/[^a-z0-9\s]/g, '');

    // Check for exact matches
    if (normalized1 === normalized2) return true;

    // Check for very close matches (e.g., "cyber attacks" vs "cyber attack")
    if (normalized1.replace(/\s+/g, '') === normalized2.replace(/\s+/g, '')) return true;

    // Check for singular/plural matches
    if (normalized1.replace(/s$/, '') === normalized2.replace(/s$/, '') ||
        normalized1 === normalized2.replace(/s$/, '') ||
        normalized1.replace(/s$/, '') === normalized2) return true;

    // Intelligent semantic matching based on core concepts
    const semanticMatches = [
      // Cyber Security
      {
        patterns: ['cyber attack', 'cyberattack', 'cybersecurity', 'data breach', 'ransomware', 'phishing'],
        match: (r1: string, r2: string) => {
          const cyber1 = r1.includes('cyber') || r1.includes('breach') || r1.includes('ransomware') || r1.includes('phishing');
          const cyber2 = r2.includes('cyber') || r2.includes('breach') || r2.includes('ransomware') || r2.includes('phishing');
          return cyber1 && cyber2;
        }
      },
      // Supply Chain
      {
        patterns: ['supply chain', 'supply disruption', 'supplier', 'hardware', 'software', 'access'],
        match: (r1: string, r2: string) => {
          const supply1 = r1.includes('supply') || r1.includes('chain') || r1.includes('supplier');
          const supply2 = r2.includes('supply') || r2.includes('chain') || r2.includes('supplier');
          return supply1 && supply2;
        }
      },
      // Personnel/Staff
      {
        patterns: ['personnel', 'staff', 'employee', 'key', 'loss', 'unavailability', 'illness', 'resignation'],
        match: (r1: string, r2: string) => {
          const personnel1 = r1.includes('personnel') || r1.includes('staff') || r1.includes('employee') || r1.includes('key');
          const personnel2 = r2.includes('personnel') || r2.includes('staff') || r2.includes('employee') || r2.includes('key');
          return personnel1 && personnel2;
        }
      },
      // Equipment/Infrastructure
      {
        patterns: ['equipment', 'hardware', 'infrastructure', 'server', 'network', 'system', 'failure'],
        match: (r1: string, r2: string) => {
          const equip1 = r1.includes('equipment') || r1.includes('hardware') || r1.includes('infrastructure') || r1.includes('server') || r1.includes('system');
          const equip2 = r2.includes('equipment') || r2.includes('hardware') || r2.includes('infrastructure') || r2.includes('server') || r2.includes('system');
          return equip1 && equip2;
        }
      },
      // Data/Information
      {
        patterns: ['data', 'information', 'customer data', 'system failure', 'security breach'],
        match: (r1: string, r2: string) => {
          const data1 = r1.includes('data') || r1.includes('information') || r1.includes('customer');
          const data2 = r2.includes('data') || r2.includes('information') || r2.includes('customer');
          return data1 && data2;
        }
      },
      // Natural Disasters
      {
        patterns: ['natural disaster', 'earthquake', 'flood', 'storm', 'hurricane', 'wildfire', 'extreme weather', 'severe weather'],
        match: (r1: string, r2: string) => {
          const natural1 = r1.includes('natural') || r1.includes('earthquake') || r1.includes('flood') || r1.includes('storm') || r1.includes('hurricane') || r1.includes('wildfire') || r1.includes('weather');
          const natural2 = r2.includes('natural') || r2.includes('earthquake') || r2.includes('flood') || r2.includes('storm') || r2.includes('hurricane') || r2.includes('wildfire') || r2.includes('weather');
          return natural1 && natural2;
        }
      },
      // Regulatory/Compliance
      {
        patterns: ['regulatory', 'compliance', 'regulation', 'legal', 'environmental', 'safety', 'liability'],
        match: (r1: string, r2: string) => {
          const reg1 = r1.includes('regulatory') || r1.includes('compliance') || r1.includes('regulation') || r1.includes('legal') || r1.includes('environmental') || r1.includes('safety') || r1.includes('liability');
          const reg2 = r2.includes('regulatory') || r2.includes('compliance') || r2.includes('regulation') || r2.includes('legal') || r2.includes('environmental') || r2.includes('safety') || r2.includes('liability');
          return reg1 && reg2;
        }
      },
      // Economic/Financial
      {
        patterns: ['economic', 'financial', 'downturn', 'recession', 'market', 'demand', 'revenue'],
        match: (r1: string, r2: string) => {
          const econ1 = r1.includes('economic') || r1.includes('financial') || r1.includes('downturn') || r1.includes('recession') || r1.includes('market') || r1.includes('demand') || r1.includes('revenue');
          const econ2 = r2.includes('economic') || r2.includes('financial') || r2.includes('downturn') || r2.includes('recession') || r2.includes('market') || r2.includes('demand') || r2.includes('revenue');
          return econ1 && econ2;
        }
      },
      // Intellectual Property
      {
        patterns: ['intellectual property', 'ip', 'patent', 'trademark', 'copyright', 'theft', 'infringement'],
        match: (r1: string, r2: string) => {
          const ip1 = r1.includes('intellectual') || r1.includes('property') || r1.includes('patent') || r1.includes('trademark') || r1.includes('copyright') || r1.includes('theft') || r1.includes('infringement');
          const ip2 = r2.includes('intellectual') || r2.includes('property') || r2.includes('patent') || r2.includes('trademark') || r2.includes('copyright') || r2.includes('theft') || r2.includes('infringement');
          return ip1 && ip2;
        }
      },
      // Workplace/Safety
      {
        patterns: ['workplace', 'safety', 'accident', 'injury', 'health', 'safety violation'],
        match: (r1: string, r2: string) => {
          const work1 = r1.includes('workplace') || r1.includes('safety') || r1.includes('accident') || r1.includes('injury') || r1.includes('health');
          const work2 = r2.includes('workplace') || r2.includes('safety') || r2.includes('accident') || r2.includes('injury') || r2.includes('health');
          return work1 && work2;
        }
      }
    ];

    // Check semantic matches
    for (const semanticMatch of semanticMatches) {
      if (semanticMatch.match(normalized1, normalized2)) {
        return true;
      }
    }

    return false;
  }, []);

  // Get AI suggestions for risks and critical functions
  const getAISuggestions = useCallback(async () => {
    try {
      setIsLoadingSuggestions(true);

      let actualBcpId: string;

      if (bcpId) {
        actualBcpId = bcpId;
      } else {
        const savedBCP = await createBCP(formData);
        actualBcpId = savedBCP.id.toString();
        setBcpId(actualBcpId);
      }

      const suggestionsData = await getBCPRiskSuggestions(actualBcpId);
      setSuggestions(suggestionsData.suggestions);

      if (suggestionsData.suggestions) {
        const newRisks = { ...risks };

        suggestionsData.suggestions.suggested_risks?.forEach(aiRisk => {
          const defaultRiskMatch = defaultRisks.find(defaultRisk =>
            isSimilarRisk(aiRisk, defaultRisk)
          );

          if (defaultRiskMatch) {
            if (!newRisks.primary_concerns.includes(defaultRiskMatch)) {
              newRisks.primary_concerns.push(defaultRiskMatch);
            }
          } else {
            const existingMatch = newRisks.primary_concerns.find(existingRisk =>
              isSimilarRisk(aiRisk, existingRisk)
            );

            if (!existingMatch) {
              newRisks.primary_concerns.push(aiRisk);
            }
          }
        });

        suggestionsData.suggestions.location_specific_risks?.forEach(aiLocationRisk => {
          const defaultLocationRiskMatch = defaultLocationRisks.find(defaultLocationRisk =>
            isSimilarRisk(aiLocationRisk, defaultLocationRisk)
          );

          if (defaultLocationRiskMatch) {
            if (!newRisks.location_risks.includes(defaultLocationRiskMatch)) {
              newRisks.location_risks.push(defaultLocationRiskMatch);
            }
          } else {
            const existingLocationMatch = newRisks.location_risks.find(existingLocationRisk =>
              isSimilarRisk(aiLocationRisk, existingLocationRisk)
            );

            if (!existingLocationMatch) {
              newRisks.location_risks.push(aiLocationRisk);
            }
          }
        });

        setRisks(newRisks);
      }
    } catch (error: unknown) {
      const err = error as { message?: string; response?: { status?: number } };
      logger.error(
        'Failed to get AI suggestions',
        { message: err.message, status: err.response?.status },
        'BCP_FORM'
      );
      alert('Failed to get AI suggestions. Please try again.');
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [bcpId, formData, risks, setBcpId, setSuggestions, setRisks, setIsLoadingSuggestions, isSimilarRisk]);

  // Apply AI suggestions to the form
  const applySuggestions = useCallback(() => {
    if (!suggestions) return;

    // Apply suggested risks (add to existing, don't replace)
    if (suggestions.suggested_risks.length > 0) {
      setRisks(prev => ({
        ...prev,
        primary_concerns: Array.from(new Set([...prev.primary_concerns, ...suggestions.suggested_risks]))
      }));
    }

    // Apply suggested critical functions
    if (suggestions.suggested_critical_functions.length > 0) {
      const criticalFunctions = suggestions.suggested_critical_functions.map(funcName => ({
        id: Math.random().toString(36).substr(2, 9),
        name: funcName,
        description: `AI-suggested critical function: ${funcName}`,
        criticality: 'high' as const,
        max_downtime: '4 hours',
        responsible_role: 'Department Manager',
        responsible_team: 'Operations',
        required_resources: ['Staff', 'Equipment', 'Systems'],
        resources: ['Staff', 'Equipment', 'Systems'],
        rto: '4 hours',
        rpo: '1 hour',
        owner: 'Operations Manager',
        dependencies: []
      }));

      setFormData(prev => ({
        ...prev,
        critical_functions: criticalFunctions
      }));
    }

    setSuggestionsApplied(true);
    logger.debug('AI suggestions applied to form', undefined, 'BCP_FORM');

    // Clear the flag after 3 seconds
    if (suggestionsTimeoutRef.current) {
      clearTimeout(suggestionsTimeoutRef.current);
    }
    suggestionsTimeoutRef.current = setTimeout(() => setSuggestionsApplied(false), 3000);
  }, [suggestions, setRisks, setFormData, setSuggestionsApplied]);

  return {
    getAISuggestions,
    applySuggestions,
    suggestions,
    isLoadingSuggestions,
    suggestionsApplied,
    setSuggestions,
    setSuggestionsApplied,
  };
};
